import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { ChevronDown } from "lucide-react";

// Flag component that renders SVG flags
const Flag = ({ code, size = 20 }) => {
  const { languageFlags } = useLanguage();
  return (
    <img 
      src={languageFlags[code]} 
      alt={code} 
      style={{ 
        width: size, 
        height: Math.round(size * 0.67), 
        borderRadius: 2,
        objectFit: 'cover',
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
      }} 
    />
  );
};

const LanguageSwitcher = ({ compact = false, flagOnly = false, style = {} }) => {
  const { language, setLanguage, languages, languageFlags } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 200 });

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const dropdownHeight = Object.keys(languages).length * 48;
      const dropdownWidth = 200;
      
      let top = rect.bottom + 8;
      if (top + dropdownHeight > viewportHeight) {
        top = rect.top - dropdownHeight - 8;
      }
      
      let left = rect.left;
      if (flagOnly || compact) {
        left = rect.right - dropdownWidth;
      }
      if (left + dropdownWidth > viewportWidth - 8) {
        left = viewportWidth - dropdownWidth - 8;
      }
      if (left < 8) {
        left = 8;
      }
      
      setPosition({
        top: top,
        left: left,
        width: dropdownWidth
      });
    }
  }, [compact, flagOnly, languages]);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, updatePosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.getElementById("lang-switcher-dropdown");
      if (buttonRef.current && !buttonRef.current.contains(event.target)) {
        if (!dropdown || !dropdown.contains(event.target)) {
          setIsOpen(false);
        }
      }
    };
    
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("touchstart", handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleSelect = (lang) => {
    setLanguage(lang);
    setIsOpen(false);
  };

  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isOpen) {
      updatePosition();
    }
    setIsOpen(!isOpen);
  };

  const dropdown = isOpen ? ReactDOM.createPortal(
    <div 
      id="lang-switcher-dropdown"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        width: position.width,
        background: "white",
        borderRadius: "12px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.15), 0 8px 40px rgba(0,0,0,0.1)",
        border: "1px solid rgba(0,0,0,0.08)",
        overflow: "hidden",
        zIndex: 2147483647,
        animation: "fadeInScale 0.15s ease-out"
      }}
    >
      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.95) translateY(-4px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
      {Object.entries(languages).map(([code, name]) => (
        <button
          key={code}
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSelect(code);
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            width: "100%",
            padding: "0.75rem 1rem",
            border: "none",
            background: language === code ? "#f0f4ff" : "white",
            cursor: "pointer",
            fontSize: "0.95rem",
            textAlign: "left",
            transition: "background 0.1s ease"
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = language === code ? "#e8edff" : "#f8f9fa"}
          onMouseLeave={(e) => e.currentTarget.style.background = language === code ? "#f0f4ff" : "white"}
        >
          <Flag code={code} size={24} />
          <span style={{ color: "#1a1a2e", fontWeight: language === code ? 600 : 400 }}>{name}</span>
          {language === code && <span style={{ marginLeft: "auto", color: "#6366f1", fontWeight: 600 }}>✓</span>}
        </button>
      ))}
    </div>,
    document.body
  ) : null;

  // Flag only mode - just the flag icon, clickable
  if (flagOnly) {
    return (
      <>
        <button
          ref={buttonRef}
          onClick={toggleDropdown}
          type="button"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "4px",
            background: "transparent",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            transition: "transform 0.2s",
            ...style
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.1)"}
          onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
          title="Change language"
        >
          <Flag code={language} size={22} />
        </button>
        {dropdown}
      </>
    );
  }

  if (compact) {
    return (
      <>
        <button
          ref={buttonRef}
          onClick={toggleDropdown}
          type="button"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.25rem",
            padding: "0.5rem",
            background: "rgba(255,255,255,0.1)",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: "8px",
            cursor: "pointer",
            transition: "background 0.2s",
            ...style
          }}
        >
          <Flag code={language} size={22} />
        </button>
        {dropdown}
      </>
    );
  }

  return (
    <>
      <button
        ref={buttonRef}
        onClick={toggleDropdown}
        type="button"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "0.75rem 1rem",
          background: "white",
          border: "2px solid #e5e7eb",
          borderRadius: "10px",
          cursor: "pointer",
          fontSize: "0.95rem",
          fontWeight: 500,
          width: "100%",
          justifyContent: "space-between",
          transition: "border-color 0.2s, box-shadow 0.2s",
          ...style
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "#667eea";
          e.currentTarget.style.boxShadow = "0 0 0 3px rgba(102,126,234,0.1)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "#e5e7eb";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Flag code={language} size={24} />
          <span style={{ color: "#1a1a2e" }}>{languages[language]}</span>
        </div>
        <ChevronDown 
          size={18} 
          style={{ 
            color: "#6b7280",
            transform: isOpen ? "rotate(180deg)" : "none", 
            transition: "transform 0.2s ease" 
          }} 
        />
      </button>
      {dropdown}
    </>
  );
};

export default LanguageSwitcher;
